import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookOpen, LogOut, Upload, Loader2, FileUp, Shield, Mail, Clock, Library, LayoutGrid } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo, DevCredit } from "@/components/landing/Logo";
import { BookLoader } from "@/components/BrandLoader";
import { GridCard } from "@/components/library/GridCard";
import { Bookshelf } from "@/components/library/Bookshelf";

const ViewToggle = ({ view, setView }) => (
  <div className="neu-inset flex rounded-full p-1" role="tablist" data-testid="view-toggle">
    {[["shelf", Library, "Shelf"], ["grid", LayoutGrid, "Grid"]].map(([k, I, l]) => (
      <button key={k} onClick={() => setView(k)} role="tab" aria-selected={view === k} data-testid={`view-${k}-btn`} className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${view === k ? "neu-btn-primary" : "text-[#64748B] hover:text-[#0F172A]"}`}><I className="h-3.5 w-3.5" />{l}</button>
    ))}
  </div>
);

const LicenseBanner = ({ user }) => {
  if (!user || user.is_admin || !user.license) return null;
  const left = Math.ceil((new Date(user.license.expires_at) - Date.now()) / 86400000);
  const expired = !user.license_active;
  return (
    <div className={`mb-10 flex flex-wrap items-center justify-between gap-4 rounded-3xl p-5 ${expired ? "bg-[#A62B8A]/10 ring-2 ring-[#A62B8A]/40" : "neu-raised"}`} data-testid="license-banner">
      <div className="flex items-center gap-3">
        <Clock className={`h-5 w-5 ${expired ? "text-[#A62B8A]" : "text-[#F7933E]"}`} />
        <p className="text-sm"><b>{expired ? "Your license has expired." : user.license.plan === "trial" ? `Free trial: ${left} day${left === 1 ? "" : "s"} left.` : `License active: ${left} days left.`}</b>{" "}
          <span className="text-[#64748B]">{expired ? "You can still sign in, but uploading, editing and publishing are paused and your books are offline." : `Renews or upgrades to 1, 3, 6 or 12 months via the admin.`}</span></p>
      </div>
      <Link to="/contact?kind=license" className="neu-btn-primary rounded-full px-5 py-2 text-xs font-bold" data-testid="license-request-btn">Request license</Link>
    </div>
  );
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [books, setBooks] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [shelves, setShelves] = useState([]);
  const [view, setViewState] = useState(() => localStorage.getItem("ibs_library_view") || "shelf");
  const input = useRef();
  const setView = (v) => { setViewState(v); localStorage.setItem("ibs_library_view", v); };

  const load = useCallback(() => api.get("/books").then((r) => setBooks(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); api.get("/shelves").then((r) => setShelves(r.data)).catch(() => {}); }, [load]);

  const moveBook = async (bookId, shelfId) => {
    const prev = books;
    setBooks((bs) => bs.map((b) => (b.book_id === bookId ? { ...b, shelf_id: shelfId } : b)));
    try {
      await api.put(`/books/${bookId}/shelf`, { shelf_id: shelfId });
      toast.success(`Moved to ${shelves.find((s) => s.shelf_id === shelfId)?.name || "Unsorted"}`);
    } catch (e) { setBooks(prev); toast.error(errMsg(e)); }
  };
  const createShelf = async (name) => {
    try { const { data } = await api.post("/shelves", { name }); setShelves((ss) => [...ss, data]); toast.success(`Shelf "${name}" created`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const renameShelf = async (id, name) => {
    try { await api.patch(`/shelves/${id}`, { name }); setShelves((ss) => ss.map((s) => (s.shelf_id === id ? { ...s, name } : s))); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const deleteShelf = async (shelf) => {
    if (!window.confirm(`Delete shelf "${shelf.name}"? Its books move to Unsorted.`)) return;
    try {
      await api.delete(`/shelves/${shelf.shelf_id}`);
      setShelves((ss) => ss.filter((s) => s.shelf_id !== shelf.shelf_id));
      setBooks((bs) => bs.map((b) => (b.shelf_id === shelf.shelf_id ? { ...b, shelf_id: "" } : b)));
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => {
    if (!books?.some((b) => b.status === "processing")) return;
    const t = setTimeout(load, 1500);
    return () => clearTimeout(t);
  }, [books, load]);

  const IMG = /\.(jpe?g|png|webp|gif|bmp|tiff?)$/i;
  const upload = async (list) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    const pdfs = files.filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    const imgs = files.filter((f) => IMG.test(f.name));
    if (!((pdfs.length === 1 && files.length === 1) || (imgs.length && imgs.length === files.length))) return toast.error("Choose one PDF, or one or more images (JPG, PNG, WebP…)");
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/books/upload", fd);
      toast.success(imgs.length ? `${imgs.length} image${imgs.length > 1 ? "s" : ""} uploaded — building your book` : "Upload complete — generating optimized pages");
      setBooks((bs) => [data, ...(bs || [])]);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
      if (input.current) input.current.value = "";
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    await api.delete(`/books/${b.book_id}`);
    setBooks((bs) => bs.filter((x) => x.book_id !== b.book_id));
    toast.success("Book deleted");
  };

  return (
    <div className="min-h-screen bg-[#EEF2F8]" data-testid="dashboard-page">
      <header className="sticky top-0 z-20 bg-[#EEF2F8]/80 shadow-[0_6px_16px_-10px_rgba(166,180,208,.9)] backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-6 md:px-12">
          <Link to="/dashboard"><Logo /></Link>
          <div className="flex items-center gap-3">
            {user?.is_admin && <Link to="/admin" className="neu-btn flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-[#3D3DF0]" data-testid="admin-link"><Shield className="h-3.5 w-3.5" />Admin</Link>}
            <Link to="/contact" className="neu-btn flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold" data-testid="contact-link"><Mail className="h-3.5 w-3.5" />Contact</Link>
            {user?.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />}
            <span className="hidden text-sm text-[#64748B] sm:inline" data-testid="user-name">{user?.name}</span>
            <button onClick={async () => { await logout(); navigate("/"); }} className="rounded p-2 text-[#64748B] hover:bg-[#E2E8F3] hover:text-[#0F172A]" aria-label="Log out" data-testid="logout-btn"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 py-14 md:px-12">
        <LicenseBanner user={user} />
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#64748B]">Library</p>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">My Books</h1>
          </div>
          <div className="flex flex-wrap items-center gap-4">
          <ViewToggle view={view} setView={setView} />
          <button onClick={() => input.current?.click()} disabled={uploading || !user?.license_active} data-testid="upload-pdf-btn" className="flex items-center gap-2 rounded-full neu-btn-primary px-6 py-3 text-sm font-medium disabled:opacity-60">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {uploading ? "Uploading…" : "Upload PDF or images"}
          </button>
          <input ref={input} type="file" multiple accept="application/pdf,.pdf,image/jpeg,image/png,image/webp,image/gif,image/bmp,image/tiff" hidden onChange={(e) => upload(e.target.files)} data-testid="upload-pdf-input" />
          </div>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
          className={`mt-10 flex items-center gap-4 rounded-3xl neu-inset border-2 border-dashed p-8 transition-colors ${drag ? "border-[#3D3DF0] bg-[#3D3DF0]/10" : "border-[#3D3DF0]/30"}`}
          data-testid="upload-dropzone"
        >
          <FileUp className="h-6 w-6 text-[#64748B]" />
          <p className="text-sm text-[#64748B]">Drop a PDF — or select several images (JPG, PNG, WebP) to make one page each, ordered by file name. Textbooks, manuals, catalogues, comics, children’s books… every page is converted to fast multi-resolution WebP on upload.</p>
        </div>

        {books === null ? (
          <div className="mt-16 flex flex-col items-center gap-3" data-testid="library-loading"><BookLoader size={44} /><p className="text-xs text-[#64748B]">Loading your library…</p></div>
        ) : books.length === 0 ? (
          <div className="mt-20 text-center" data-testid="empty-library">
            <BookOpen className="mx-auto h-10 w-10 text-[#2E2E2E]" />
            <p className="mt-4 font-display text-2xl font-bold">No books yet</p>
            <p className="mt-1 text-sm text-[#64748B]">Upload your first PDF to begin.</p>
          </div>
        ) : (
          view === "shelf" ? (
            <div className="mt-12"><Bookshelf books={books} shelves={shelves} canEdit={!!user?.license_active} onMove={moveBook} onDeleteBook={remove} onCreate={createShelf} onRename={renameShelf} onDelete={deleteShelf} /></div>
          ) : (
            <div className="mt-12 grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:grid-cols-5" data-testid="book-grid">
              {books.map((b) => <GridCard key={b.book_id} b={b} shelves={shelves} onDelete={remove} onMove={moveBook} />)}
            </div>
          )
        )}
      </main>
      <DevCredit className="pb-8 pt-4" />
    </div>
  );
}
